import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  isUnauthorizedApiError,
  SUPER_TOKEN_KEY,
} from '../../api/client';
import { showGrowl } from '../../ui/growlState';

type ModelPreset = {
  id: string;
  modelId: string;
  label: string;
  sortOrder: number;
  inputUsdPerMillion: number | null;
  outputUsdPerMillion: number | null;
  maxOutputTokens: number | null;
};

type AiSettings = {
  defaultChatModel: string;
  presets: ModelPreset[];
};

function isAnthropicModelId(modelId: string): boolean {
  return modelId.trim().toLowerCase().startsWith('claude-');
}

function isDeepseekModelId(modelId: string): boolean {
  return modelId.trim().toLowerCase().startsWith('deepseek-');
}

function groupPresetsByProvider(presets: ModelPreset[]): { title: string; rows: ModelPreset[] }[] {
  const openai = presets.filter((p) => !isAnthropicModelId(p.modelId) && !isDeepseekModelId(p.modelId));
  const anthropic = presets.filter((p) => isAnthropicModelId(p.modelId));
  const deepseek = presets.filter((p) => isDeepseekModelId(p.modelId));
  const out: { title: string; rows: ModelPreset[] }[] = [];
  if (openai.length) out.push({ title: 'OpenAI', rows: openai });
  if (anthropic.length) out.push({ title: 'Anthropic (Claude)', rows: anthropic });
  if (deepseek.length) out.push({ title: 'DeepSeek', rows: deepseek });
  return out;
}

export function SuperAiSettingsPage() {
  const nav = useNavigate();
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  const [data, setData] = useState<AiSettings | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [newModelId, setNewModelId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newUsdIn, setNewUsdIn] = useState('');
  const [newUsdOut, setNewUsdOut] = useState('');
  const [newMaxOut, setNewMaxOut] = useState('');

  const applySuggested = async (onlyEmpty: boolean) => {
    if (!token) return;
    setErr('');
    setBusy(true);
    try {
      await apiPost(
        '/api/super/ai-pricing/apply-suggested',
        { onlyEmpty },
        token,
        { successMessage: onlyEmpty ? 'Precios sugeridos en filas vacías' : 'Tarifas sugeridas aplicadas al catálogo' }
      );
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Añade modelos del listado base que falten y actualiza precios (mapa de referencia en el backend). */
  const refreshCatalogAndPrices = async () => {
    if (!token) return;
    setErr('');
    setBusy(true);
    try {
      const d = await apiPost<{ added: number; pricesUpdated: number }>(
        '/api/super/ai-pricing/refresh-defaults',
        {},
        token
      );
      showGrowl(
        `Sincronizado: ${d.added} modelo(s) nuevo(s) en catálogo, ${d.pricesUpdated} fila(s) con precio actualizado (referencia).`,
        'success'
      );
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setErr('');
    try {
      const d = await apiGet<{
        defaultChatModel: string;
        presets: ModelPreset[];
      }>('/api/super/ai-settings', token);
      setData({
        defaultChatModel: d.defaultChatModel,
        presets: d.presets.map((p: ModelPreset) => ({
          ...p,
          inputUsdPerMillion: p.inputUsdPerMillion ?? null,
          outputUsdPerMillion: p.outputUsdPerMillion ?? null,
          maxOutputTokens: p.maxOutputTokens ?? null,
        })),
      });
    } catch (e) {
      if (isUnauthorizedApiError(e)) {
        localStorage.removeItem(SUPER_TOKEN_KEY);
        nav('/super/login', { replace: true });
        return;
      }
      setErr((e as Error).message);
    }
  }, [token, nav]);

  useEffect(() => {
    if (!token) {
      nav('/super/login', { replace: true });
      return;
    }
    void load();
  }, [token, nav, load]);

  const addPreset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setErr('');
    try {
      const parseUsd = (s: string): number | null | undefined => {
        const t = s.trim();
        if (!t) return undefined;
        const n = Number(t.replace(',', '.'));
        return Number.isFinite(n) ? n : null;
      };
      const parseMaxOut = (s: string): number | null | undefined => {
        const t = s.trim();
        if (!t) return undefined;
        const n = parseInt(t.replace(/[^\d]/g, ''), 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const payload: Record<string, unknown> = { modelId: newModelId.trim(), label: newLabel.trim() };
      const uIn = parseUsd(newUsdIn);
      const uOut = parseUsd(newUsdOut);
      const mOut = parseMaxOut(newMaxOut);
      if (uIn !== undefined) payload.inputUsdPerMillion = uIn;
      if (uOut !== undefined) payload.outputUsdPerMillion = uOut;
      if (mOut !== undefined) payload.maxOutputTokens = mOut;
      await apiPost('/api/super/ai-model-presets', payload, token, { successMessage: 'Modelo añadido a la lista' });
      setNewModelId('');
      setNewLabel('');
      setNewUsdIn('');
      setNewUsdOut('');
      setNewMaxOut('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const updatePreset = async (
    presetId: string,
    patch: {
      modelId?: string;
      label?: string;
      sortOrder?: number;
      inputUsdPerMillion?: number | null;
      outputUsdPerMillion?: number | null;
      maxOutputTokens?: number | null;
    },
    previousModelId: string
  ) => {
    if (!token) return;
    setErr('');
    try {
      await apiPatch(`/api/super/ai-model-presets/${presetId}`, patch, token, {
        successMessage: 'Modelo de lista actualizado',
      });
      const newMid = patch.modelId?.trim();
      if (
        newMid &&
        newMid !== previousModelId.trim() &&
        data?.defaultChatModel?.trim() === previousModelId.trim()
      ) {
        await apiPatch<AiSettings>(
          '/api/super/ai-settings',
          { defaultChatModel: newMid },
          token,
          { showErrorGrowl: true }
        );
      }
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const deletePreset = async (presetId: string) => {
    if (!token) return;
    if (!window.confirm('¿Eliminar este modelo de la lista de la consola?')) return;
    setErr('');
    try {
      const victim = data?.presets.find((p) => p.id === presetId);
      const wasDefault =
        victim != null && data?.defaultChatModel?.trim() === victim.modelId.trim();
      await apiDelete(`/api/super/ai-model-presets/${presetId}`, token, { successMessage: 'Modelo quitado de la lista' });
      if (wasDefault && data) {
        const next = data.presets.filter((p) => p.id !== presetId);
        if (next.length > 0) {
          await apiPatch<AiSettings>(
            '/api/super/ai-settings',
            { defaultChatModel: next[0]!.modelId.trim() },
            token,
            { showErrorGrowl: true }
          );
        }
      }
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  if (!token) return null;

  return (
    <div style={{ flex: 1, minHeight: 0, background: '#f8fafc' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Modelos (proveedores IA)</h1>
        <p style={{ color: '#64748b', marginBottom: 24, lineHeight: 1.5 }}>
          Definí el catálogo global que verán las empresas al elegir modelo en sus agentes. Cada fila es un modelo de API
          (OpenAI, Anthropic o DeepSeek según el <code>modelId</code>). Las claves de la plataforma van en el servidor
          (<code>OPENAI_API_KEY</code>, <code>ANTHROPIC_API_KEY</code>, <code>DEEPSEEK_API_KEY</code>). Las empresas no
          gestionan claves desde su consola; el acceso IA se administra en esta pantalla y en{' '}
          <Link to="/super/orgs">Empresas</Link>.
        </p>

        {err ? (
          <p style={{ color: '#b91c1c', marginBottom: 16, fontSize: 14 }}>{err}</p>
        ) : null}

        {!data ? <p>Cargando…</p> : null}

        <section
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 20,
            border: '1px solid #e2e8f0',
          }}
        >
          <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Catálogo global por proveedor</h2>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
            <code>claude-</code> → Anthropic; <code>deepseek-</code> → DeepSeek; el resto (p. ej. <code>gpt-</code>) →
            OpenAI. Desde <Link to="/super/orgs">Empresas</Link> podés
            limitar qué entradas de este catálogo usa cada organización. <strong>USD/1M</strong> = tarifa estimada
            (USD por millón de tokens; no es facturación). <strong>Máx. sal.</strong> ajusta el límite de salida
            de la API (<code>max_tokens</code>); dejá vacío para usar el valor fijo del motor (p. ej. 400 o 2048).
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                style={btn}
                disabled={busy}
                onClick={() => void refreshCatalogAndPrices()}
                title="Añade modelos del listado base que falten y reescribe USD/1M con el mapa de referencia del servicio"
              >
                Sincronizar catálogo y precios
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
              Un solo paso: incorpora <code>modelId</code> faltantes respecto a la lista embebida y actualiza precios
              donde haya mapa. Modelos añadidos a mano no se borran. Para afinar, usá:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => void applySuggested(true)}>
                Rellenar precios vacíos (sugeridos)
              </button>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => void applySuggested(false)}>
                Solo sobrescribir precios (sugeridos)
              </button>
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Las sugeridas viven en el código; el <code>modelId</code> de cada fila debe coincidir con la clave.
            </span>
          </div>

          <form onSubmit={(e) => void addPreset(e)} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={hint}>modelId</span>
              <input
                style={inp}
                value={newModelId}
                onChange={(e) => setNewModelId(e.target.value)}
                placeholder="ej. gpt-4o o claude-sonnet-4-6"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={hint}>Etiqueta</span>
              <input
                style={{ ...inp, minWidth: 200 }}
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Visible en consola"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={hint}>USD/1M ent. (opt.)</span>
              <input
                style={{ ...inp, width: 100 }}
                inputMode="decimal"
                value={newUsdIn}
                onChange={(e) => setNewUsdIn(e.target.value)}
                placeholder="—"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={hint}>USD/1M sal. (opt.)</span>
              <input
                style={{ ...inp, width: 100 }}
                inputMode="decimal"
                value={newUsdOut}
                onChange={(e) => setNewUsdOut(e.target.value)}
                placeholder="—"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={hint} title="Límite de tokens de salida (API); vacío = por defecto del motor">
                Máx. salida (opt.)
              </span>
              <input
                style={{ ...inp, width: 100 }}
                inputMode="numeric"
                value={newMaxOut}
                onChange={(e) => setNewMaxOut(e.target.value)}
                placeholder="—"
              />
            </div>
            <button type="submit" style={btnOutline}>
              Añadir al catálogo
            </button>
          </form>

          {data && data.presets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {groupPresetsByProvider(data.presets).map((group) => (
                <div key={group.title}>
                  <h3 style={{ fontSize: 14, margin: '0 0 10px', color: '#334155' }}>{group.title}</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 6px' }}>Orden</th>
                        <th style={{ padding: '8px 6px' }}>modelId</th>
                        <th style={{ padding: '8px 6px' }}>Etiqueta</th>
                        <th style={{ padding: '8px 6px' }} title="USD por millón de tokens de entrada (estimado)">
                          USD/1M ent.
                        </th>
                        <th style={{ padding: '8px 6px' }} title="USD por millón de tokens de salida (estimado)">
                          USD/1M sal.
                        </th>
                        <th
                          style={{ padding: '8px 6px' }}
                          title="Máx. tokens de salida (API). Vacío al guardar = heredar 400/2048 del motor"
                        >
                          Máx. sal.
                        </th>
                        <th style={{ padding: '8px 6px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((p) => (
                        <ModelPresetTableRow
                          key={p.id}
                          preset={p}
                          onSave={updatePreset}
                          onDelete={deletePreset}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#94a3b8' }}>Sin modelos en el catálogo (se pueden crear arriba o al iniciar la API).</p>
          )}
        </section>
      </div>
    </div>
  );
}

function ModelPresetTableRow({
  preset,
  onSave,
  onDelete,
}: {
  preset: ModelPreset;
  onSave: (
    id: string,
    p: {
      modelId?: string;
      label?: string;
      sortOrder?: number;
      inputUsdPerMillion?: number | null;
      outputUsdPerMillion?: number | null;
      maxOutputTokens?: number | null;
    },
    previousModelId: string
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [modelId, setModelId] = useState(preset.modelId);
  const [label, setLabel] = useState(preset.label);
  const [sortOrder, setSortOrder] = useState(String(preset.sortOrder));
  const [usdIn, setUsdIn] = useState(
    preset.inputUsdPerMillion != null ? String(preset.inputUsdPerMillion) : ''
  );
  const [usdOut, setUsdOut] = useState(
    preset.outputUsdPerMillion != null ? String(preset.outputUsdPerMillion) : ''
  );
  const [maxOut, setMaxOut] = useState(
    preset.maxOutputTokens != null ? String(preset.maxOutputTokens) : ''
  );
  useEffect(() => {
    setModelId(preset.modelId);
    setLabel(preset.label);
    setSortOrder(String(preset.sortOrder));
    setUsdIn(preset.inputUsdPerMillion != null ? String(preset.inputUsdPerMillion) : '');
    setUsdOut(preset.outputUsdPerMillion != null ? String(preset.outputUsdPerMillion) : '');
    setMaxOut(preset.maxOutputTokens != null ? String(preset.maxOutputTokens) : '');
  }, [preset]);

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input
          style={{ ...inp, width: 64 }}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input style={{ ...inp, width: '100%', minWidth: 140 }} value={modelId} onChange={(e) => setModelId(e.target.value)} />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input style={{ ...inp, width: '100%', minWidth: 160 }} value={label} onChange={(e) => setLabel(e.target.value)} />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input
          style={{ ...inp, width: 80 }}
          inputMode="decimal"
          value={usdIn}
          onChange={(e) => setUsdIn(e.target.value)}
          placeholder="—"
        />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input
          style={{ ...inp, width: 80 }}
          inputMode="decimal"
          value={usdOut}
          onChange={(e) => setUsdOut(e.target.value)}
          placeholder="—"
        />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
        <input
          style={{ ...inp, width: 72 }}
          inputMode="numeric"
          value={maxOut}
          onChange={(e) => setMaxOut(e.target.value)}
          placeholder="—"
        />
      </td>
      <td style={{ padding: '8px 6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        <button
          type="button"
          style={btnSm}
          onClick={() => {
            const parseUsd = (s: string): number | null => {
              const t = s.trim();
              if (!t) return null;
              const n = Number(t.replace(',', '.'));
              return Number.isFinite(n) ? n : null;
            };
            const parseMaxTok = (s: string): number | null => {
              const t = s.trim();
              if (!t) return null;
              const n = parseInt(t.replace(/[^\d]/g, ''), 10);
              return Number.isFinite(n) && n > 0 ? n : null;
            };
            onSave(
              preset.id,
              {
                modelId: modelId.trim(),
                label: label.trim(),
                sortOrder: Number(sortOrder) || 0,
                inputUsdPerMillion: parseUsd(usdIn),
                outputUsdPerMillion: parseUsd(usdOut),
                maxOutputTokens: parseMaxTok(maxOut),
              },
              preset.modelId
            );
          }}
        >
          Guardar
        </button>
        <button type="button" style={{ ...btnSm, marginLeft: 6, color: '#b91c1c', borderColor: '#fecaca' }} onClick={() => onDelete(preset.id)}>
          Borrar
        </button>
      </td>
    </tr>
  );
}

const hint: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  marginBottom: 2,
  fontWeight: 500,
};

const inp: React.CSSProperties = {
  marginTop: 0,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  fontSize: 14,
};
const btn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnOutline: React.CSSProperties = {
  ...btn,
  background: '#fff',
  color: '#7c3aed',
  border: '1px solid #ddd6fe',
};
const btnSm: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  fontSize: 12,
  cursor: 'pointer',
};
